"use client";

/**
 * The public homepage's personal band, under the hero (owner,
 * 2026-10-02: "we should be able to determine their intent ourselves if
 * they really omit doing it themselves"). A signed-out visitor:
 *
 *   - whose intent is known (picked here, or inferred from what they
 *     browse) gets their market (VisitorMarket): the lead number, a line
 *     that says where this comes from, the next step, the real listing
 *     cards under a heading that explains them, and the roadmap for that
 *     goal;
 *   - whose intent is not known yet gets one question and six answers,
 *     and, once this visit has opened a few listings, the recommender's
 *     row under it (VisitorRecRow, loaded only when it has something).
 *
 * The pick lives in the browser and moves onto the account at sign-up
 * (lib/use-personal). Same parts as the signed-in hub, smaller.
 *
 * The market band is a chunk of its own (2026-10-19): it loads once a
 * goal is known or likely (stored from an earlier visit), on a pointer
 * or finger over the question, and with a pick; until it is here a
 * fresh pick keeps the question on screen, so nothing collapses.
 */
import { useCallback, useEffect, useState } from "react";
import { fetchForYou } from "@/lib/rec-client";
import { usableFeed, type RecAnswer } from "@/lib/rec-feed";
import { useT } from "@/lib/locale-context";
import { trackEvent } from "@/lib/tracking";
import { getCachedIntent, getVisitorGoal, usePersonal, SELECTABLE_INTENTS, type Intent } from "@/lib/use-personal";
import { GOAL_ART, GOAL_LABEL } from "./hub/hub-text";
import Illo from "@/components/Illo";
import { SELECTED } from "@/lib/ui-classes";

type MarketModule = typeof import("./VisitorMarket");
type RecRowModule = typeof import("./VisitorRecRow");
let market: MarketModule | null = null;
let marketP: Promise<MarketModule> | null = null;
function loadMarket(): Promise<MarketModule> {
  if (market) return Promise.resolve(market);
  if (!marketP) {
    marketP = import("./VisitorMarket").then(
      (m) => (market = m),
      (e: unknown) => { marketP = null; throw e; },
    );
  }
  return marketP;
}

export default function VisitorForYou() {
  const t = useT();
  const { data, choose } = usePersonal();
  const [picking, setPicking] = useState(false);
  const [Market, setMarket] = useState<MarketModule | null>(market);
  const intent = data?.intent ?? null;
  const eff = intent?.effective ?? null;

  // No goal yet: the recommender may still know this visit (its opens,
  // the page it landed on). Ask once the payload is here; load the row's
  // chunk only for a personal answer of at least three cards.
  const [recRow, setRecRow] = useState<{ answer: RecAnswer; Row: RecRowModule } | null>(null);
  useEffect(() => {
    if (!data || eff || recRow) return;
    const ctrl = new AbortController();
    let alive = true;
    void fetchForYou({ n: 7, surface: "home", signal: ctrl.signal }).then(async (ans) => {
      if (!alive || !ans || !usableFeed(ans, { min: 3, max: 3 })) return;
      const Row = await import("./VisitorRecRow");
      if (alive) setRecRow({ answer: ans, Row });
    });
    return () => { alive = false; ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per known payload without a goal
  }, [data == null, eff]);

  const warm = useCallback(() => { loadMarket().then(setMarket, () => {}); }, []);
  // A known goal, or one stored from an earlier visit, shows the band:
  // fetch its code while the personal payload is still on its way.
  useEffect(() => {
    if (Market) return;
    if (eff || getCachedIntent() || getVisitorGoal()) warm();
  }, [Market, eff, warm]);

  const pick = async (g: Intent) => {
    trackEvent("home.goal.set", { goal: g, from: intent?.explicit ?? "none", anon: 1 });
    await Promise.all([choose(g), loadMarket().then(setMarket, () => {})]);
    setPicking(false);
  };

  // The question renders from the server on: it is a fresh visitor's
  // final state, and a band that popped in a second after the payload
  // pushed the page under the short phone hero down (CLS 0.19 on the
  // phone home, 2026-10-27). A known goal swaps it for the band once.
  if (!data || !eff || picking || !Market) {
    return (
      <>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" onPointerEnter={warm} onFocus={warm} onTouchStart={warm}>
        {/* The same scenes as the signed-in picker, seven across on
            desktop and rows of three on phones, the seventh centred
            (owner, 2026-10-11: the visitor's question did not use the
            visualizations; 2026-10-21: an office and a garage joined). */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
          <p className="text-sm font-semibold text-gray-800">{t("home.foryou.ask")}</p>
          <div role="radiogroup" aria-label={t("home.ws.goal.label")} className="mt-3 grid grid-cols-6 gap-2 lg:grid-cols-7">
            {SELECTABLE_INTENTS.map((g, i) => {
              const on = g === eff;
              return (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pick(g)}
                  // Selected gets the picked tint, as on the hub's picker.
                  className={`no-scale col-span-2 flex flex-col items-center gap-2 rounded-xl border p-2 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 lg:col-span-1 ${i === 6 ? "col-start-3 lg:col-start-auto" : ""} ${on ? SELECTED : "border-gray-200 bg-white text-gray-900 hover:border-gray-300"}`}
                >
                  <span className="block h-16 w-16 overflow-hidden rounded-xl bg-[rgb(250,249,246)] dark:bg-[rgb(30,36,52)] sm:h-20 sm:w-20">
                    <Illo base={GOAL_ART[g]} className="h-full w-full object-contain" />
                  </span>
                  <span className="text-xs font-semibold leading-snug">{t(GOAL_LABEL[g])}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
      {recRow && !eff && <div className="mt-6"><recRow.Row.default answer={recRow.answer} /></div>}
      </>
    );
  }

  return <Market.default data={data} eff={eff} onChange={() => setPicking(true)} />;
}
